import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import getAppConfig from "@liberty-fund/config";

import { analytics } from "./analytics";
import { getPageInfo, isTrackedPath } from "./pages";

/**
 * Session-level tracking that isn't tied to any one click: identity, sign-in
 * outcome and page views. Rendered once in App.tsx, inside the router and
 * Auth0Provider but *outside* RequireAuth, so a failed or denied sign-in —
 * which RequireAuth renders instead of the app — is still recorded.
 *
 * Identity is set before the first `Page Viewed` (effects run in declaration
 * order, and page views wait for the session), so every page view is
 * attributed to the signed-in user rather than an anonymous id.
 */
export default function AnalyticsTracker() {
  useAuthAnalytics();
  usePageViewTracking();
  return null;
}

function useAuthAnalytics() {
  const { isAuthenticated, isLoading, user, error } = useAuth0();
  const signedInTracked = useRef(false);
  const lastErrorTracked = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    // Same precedence as concierge-app/concierge-web, so a person's
    // distinct_id is the same string in every Liberty Fund Mixpanel project.
    const distinctId = user?.email?.trim() || user?.sub;
    if (isAuthenticated && distinctId && !signedInTracked.current) {
      signedInTracked.current = true;

      const { rolesKey } = getAppConfig().auth0.customClaims;
      const roles = (user?.[rolesKey] as string[] | undefined) ?? [];
      const portalApps = [
        ...new Set(
          roles
            .filter((role) => role.startsWith("portal:"))
            .map((role) => role.split(":")[1]),
        ),
      ];

      analytics.identify(distinctId, {
        $email: user?.email,
        $name: user?.name,
        $picture: user?.picture,
        roles,
        portal_apps: portalApps,
      });
      // Also a super property, so any event can be broken down by which
      // sub-apps the user has access to without a profile join.
      analytics.register({ portal_apps: portalApps });
      analytics.track("User Signed In");
    }

    if (!isAuthenticated) {
      signedInTracked.current = false;
    }
  }, [isAuthenticated, isLoading, user]);

  useEffect(() => {
    // `error` is shared Auth0 state: a background getAccessTokenSilently()
    // failure sets it too, while the user stays signed in and the app keeps
    // rendering. Only a failure that leaves the user signed out is a failed
    // sign-in.
    if (!error || isLoading || isAuthenticated) return;
    if (lastErrorTracked.current === error.message) return;
    lastErrorTracked.current = error.message;

    // Auth0 errors carry an OAuth code (e.g. access_denied, invalid_grant)
    // plus a description; plain Errors only have a message.
    const oauthError = error as Error & {
      error?: string;
      error_description?: string;
    };
    if (oauthError.error === "access_denied") {
      analytics.track("Access Denied Viewed");
      return;
    }
    analytics.track("Login Failed", {
      error_code: oauthError.error || error.message,
      error_message: oauthError.error_description || error.message,
    });
  }, [error, isAuthenticated, isLoading]);
}

function usePageViewTracking() {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth0();
  const previousPage = useRef<string | undefined>(undefined);
  // StrictMode re-runs effects on mount in development; without this the
  // first page of every session would be counted twice there.
  const lastTrackedPath = useRef<string | null>(null);

  // Keyed on pathname only: hash changes are tab switches and modal state,
  // which have their own events (`Tab Selected`, etc.), and query strings
  // carry nothing page-identifying in this app.
  useEffect(() => {
    if (!isAuthenticated || !isTrackedPath(pathname)) return;
    if (lastTrackedPath.current === pathname) return;
    lastTrackedPath.current = pathname;

    const info = getPageInfo(pathname);
    analytics.track("Page Viewed", {
      ...info,
      previous_page: previousPage.current,
    });
    previousPage.current = info.page;
  }, [isAuthenticated, pathname]);
}
